import React, { useState } from "react";
import { Edit, Trash2, Search } from "lucide-react";
import { categoryService } from "../../services/categoryService";
import CategoryForm from "./CategoryForm";
import DeleteConfirmation from "../common/DeleteConfirmation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CategoryList = ({ categories, viewMode, setCategories }) => {
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // All categories are already in memory, so search is a client-side filter
  const filteredCategories = searchTerm
    ? categories.filter((cat) =>
        cat.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : categories;

  const handleDeleteCategory = async () => {
    try {
      setLoading(true);
      await categoryService.deleteCategory(selectedCategory.id);
      setCategories(categories.filter((cat) => cat.id !== selectedCategory.id));
      setIsDeleteModalOpen(false);
      toast.success("Category deleted successfully!");
    } catch (error) {
      console.error("Failed to delete category:", error);
      toast.error("Failed to delete category!");
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = async (formData) => {
    try {
      setLoading(true);
      const updatedCategory = await categoryService.updateCategory(
        selectedCategory.id,
        formData
      );
      setCategories(
        categories.map((cat) =>
          cat.id === updatedCategory.id ? updatedCategory : cat
        )
      );
      setIsEditModalOpen(false);
      toast.success("Category updated successfully!");
    } catch (error) {
      console.error("Failed to update category:", error);
      toast.error("Failed to update category!");
    } finally {
      setLoading(false);
    }
  };

  const renderGridView = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="spinner border-t-4 border-blue-500 w-8 h-8 rounded-full animate-spin"></div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {filteredCategories.map((category) => (
          <div
            key={category.id}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
          >
            <img
              src={category.image}
              alt={category.name}
              className="w-full h-48 object-cover"
            />
            <div className="p-4">
              <h3 className="text-xl font-bold mb-2">{category.name}</h3>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {category.subCategories?.length || 0} Sub-categories
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedCategory(category);
                      setIsEditModalOpen(true);
                    }}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCategory(category);
                      setIsDeleteModalOpen(true);
                    }}
                    className="text-red-500 hover:text-red-700"
                    disabled={loading}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderListView = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="spinner border-t-4 border-blue-500 w-8 h-8 rounded-full animate-spin"></div>
        </div>
      );
    }
    return (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {filteredCategories.map((category) => (
          <div
            key={category.id}
            className="flex items-center justify-between p-4 border-b hover:bg-gray-50 transition"
          >
            <div className="flex items-center space-x-4">
              <img
                src={category.image}
                alt={category.name}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <h3 className="text-lg font-semibold">{category.name}</h3>
                <p className="text-sm text-gray-500">
                  {category.subCategories?.length || 0} Sub-categories
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setSelectedCategory(category);
                  setIsEditModalOpen(true);
                }}
                className="text-blue-500 hover:text-blue-700"
              >
                <Edit size={20} />
              </button>
              <button
                onClick={() => {
                  setSelectedCategory(category);
                  setIsDeleteModalOpen(true);
                }}
                className="text-red-500 hover:text-red-700"
                disabled={loading}
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 pr-4 border rounded-lg focus:outline-none focus:border-blue-500"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
      </div>

      {viewMode === "grid" ? renderGridView() : renderListView()}

      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteCategory}
        itemName="category"
      />

      {selectedCategory && (
        <CategoryForm
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditCategory}
          initialData={selectedCategory}
        />
      )}
    </>
  );
};

export default CategoryList;
